/**
 * Path mode. On the shared TutorLoop host (`VITE_TUTORLOOP_PATH_HOST`) an
 * institute lives under `/<slug>`: that first segment is the base path for
 * the router, the API, media, the manifest and the service worker scope.
 * On every other host (own domain, `platform.drtutor.uk`) the base path is
 * empty and nothing changes.
 *
 * Detected once at boot from `window.location`; `reconcileBasePath` checks it
 * against the `base_path` the backend returns from `GET /tenant/`.
 */

const PATH_HOST = (import.meta.env.VITE_TUTORLOOP_PATH_HOST as string | undefined)?.trim().toLowerCase() ?? "";

export const onPathHost: boolean = PATH_HOST !== "" && window.location.hostname.toLowerCase() === PATH_HOST;

function detect(): string {
  if (!onPathHost) return "";
  const first = window.location.pathname.split("/")[1] ?? "";
  return first ? `/${first}` : "";
}

/** `""` off the path host or when no slug is in the address, else `"/<slug>"`. */
export const basePath: string = detect();

/** On the path host with no institute in the address: show the unknown-workspace screen. */
export const missingSlug: boolean = onPathHost && basePath === "";

/** The institute slug in the address, or `""` outside path mode. */
export const pathSlug: string = basePath.slice(1);

/** Adds the base path to an app-absolute path such as `/media/x.png`. */
export function withBase(path: string): string {
  return `${basePath}${path}`;
}

/** Where a file from `public/` (built under `/ws/`) is requested from. */
export function publicUrl(file: string): string {
  return `${import.meta.env.BASE_URL}${file.replace(/^\//, "")}`;
}

/**
 * Root of the API. Path mode: same origin under the slug. Otherwise today's
 * behaviour (`VITE_API_URL` if set, else `/api/v1`).
 */
export const API_BASE: string = onPathHost
  ? `${basePath}/api/v1`
  : ((import.meta.env.VITE_API_URL as string | undefined) ?? "/api/v1");

/** Makes a backend-returned media path work in path mode. Absolute URLs pass through. */
export function resolveMediaUrl<T extends string | null | undefined>(url: T): T {
  if (!onPathHost || !basePath || typeof url !== "string" || !url.startsWith("/") || url.startsWith("//")) return url;
  if (url === basePath || url.startsWith(`${basePath}/`)) return url;
  return withBase(url) as T;
}

/** Key for localStorage and sessionStorage. Unchanged off the path host. */
export function scopedKey(key: string): string {
  return onPathHost && pathSlug ? `tl.${pathSlug}.${key}` : key;
}

/**
 * If the backend names a different base path than the address, move to it
 * (keeping the rest of the path). Returns true when a redirect started.
 */
export function reconcileBasePath(serverBase: string | null | undefined): boolean {
  if (!onPathHost || typeof serverBase !== "string" || serverBase === "" || serverBase === basePath) return false;
  if (!serverBase.startsWith("/")) return false;
  const rest = basePath ? window.location.pathname.slice(basePath.length) : window.location.pathname;
  window.location.replace(`${serverBase}${rest || "/"}${window.location.search}${window.location.hash}`);
  return true;
}
